import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { api } from '../api/client';
import { fmtCHF2, fmtPct, fmtDateFr, fmtMoisFr } from '../utils/format';
import { deriverFonction } from '../utils/fonctions';
import {
  Categorie, EmployeDetail, LigneEmploye, ScenarioBudget, Tag,
} from '../types';
import { ChoixTagScenario } from './TagScenarioRevenu';

type Onglet = 'salaire' | 'repartition' | 'infos';

interface Props {
  employeId: number;
  annee: number;
  moisInitial: number; // 1..12
  // Scénario de lecture courant : une hypothèse projetée s'y rattache par défaut
  scenarioId: number | null;
  scenarios: ScenarioBudget[];
  categories: Categorie[];
  ligne: LigneEmploye | null; // ligne de grille (valeurs en vigueur par mois)
  onFermer: () => void;
  onModifie: () => void;
}

function BadgeTag({ tag }: { tag: Tag }) {
  return tag === 'definitif'
    ? <span className="badge badge-ok badge-sm">définitif</span>
    : <span className="badge badge-warn badge-sm">projeté</span>;
}

export function EmployeModal({ employeId, annee, moisInitial, scenarioId, scenarios, categories, ligne, onFermer, onModifie }: Props) {
  const [onglet, setOnglet] = useState<Onglet>('salaire');
  const [detail, setDetail] = useState<EmployeDetail | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    api.get<EmployeDetail>(`/salaires/employes/${employeId}`)
      .then(setDetail)
      .catch((e) => setErreur(e.message));
  }, [employeId]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  // ── Formulaire « modifier dès… » ─────────────────────────────────────────
  const [moisEffet, setMoisEffet] = useState(`${annee}-${String(moisInitial).padStart(2, '0')}`);
  const celluleRef = ligne?.mois[Math.max(0, Math.min(11, Number(moisEffet.slice(5, 7)) - 1))];
  const [nominal, setNominal] = useState<string>('');
  const [tauxPct, setTauxPct] = useState<string>('');
  // Le statut par défaut suit la lecture affichée dans la grille (cf. Revenus)
  const [tag, setTag] = useState<'definitif' | 'projete'>(scenarioId != null ? 'projete' : 'definitif');
  const [scenarioCible, setScenarioCible] = useState<number | null>(scenarioId);
  const [commentaire, setCommentaire] = useState('');

  useEffect(() => {
    // Préremplit avec les valeurs en vigueur au mois choisi (si année affichée)
    if (celluleRef) {
      setNominal(String(Math.round(celluleRef.nominal * 100) / 100));
      setTauxPct(String(Math.round(celluleRef.taux * 1000) / 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moisEffet, employeId]);

  const nominalNum = Number(nominal);
  const tauxNum = Number(tauxPct) / 100;
  const nominalChange = celluleRef ? Math.abs(nominalNum - celluleRef.nominal) > 0.004 : true;
  const tauxChange = celluleRef ? Math.abs(tauxNum - celluleRef.taux) > 0.00004 : true;
  const formulaireValide =
    /^\d{4}-\d{2}$/.test(moisEffet) &&
    nominalNum >= 0 && tauxNum >= 0 && tauxNum <= 3 &&
    commentaire.trim().length > 0 &&
    (tag !== 'projete' || scenarioCible != null) &&
    (nominalChange || tauxChange);

  async function enregistrerChangement() {
    if (!formulaireValide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      if (nominalChange) {
        await api.post(`/salaires/employes/${employeId}/salaire-evenements`, {
          moisEffet, montant: nominalNum, tag,
          scenarioId: tag === 'projete' ? scenarioCible : null,
          commentaire: commentaire.trim(),
        });
      }
      if (tauxChange) {
        await api.post(`/salaires/employes/${employeId}/taux-evenements`, {
          moisEffet, taux: tauxNum, tag,
          scenarioId: tag === 'projete' ? scenarioCible : null,
          commentaire: commentaire.trim(),
        });
      }
      setCommentaire('');
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  async function supprimerEvenement(type: 'salaire' | 'taux' | 'repartition', id: number) {
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      const chemin = type === 'salaire' ? `/salaires/salaire-evenements/${id}`
        : type === 'taux' ? `/salaires/taux-evenements/${id}`
        : `/salaires/repartitions/${id}`;
      await api.delete(chemin, { commentaire: c.trim() });
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  // ── Répartition (lecture seule ici : la saisie vit dans le module RH) ────
  const nomCategorie = useCallback((id: number) => {
    const c = categories.find((x) => x.id === id);
    if (!c) return `#${id}`;
    const parent = c.parentId ? categories.find((x) => x.id === c.parentId) : null;
    return parent ? `${parent.nom} — ${c.nom}` : c.nom;
  }, [categories]);

  // ── Infos ────────────────────────────────────────────────────────────────
  const [infoNom, setInfoNom] = useState('');
  const [infoDebut, setInfoDebut] = useState('');
  const [infoFin, setInfoFin] = useState('');
  const [infoNote, setInfoNote] = useState('');
  const [infoCommentaire, setInfoCommentaire] = useState('');

  // La fonction n'est pas éditable ici : elle dérive de la répartition par
  // fonction en vigueur (catégorie racine dominante) ; à défaut, la catégorie
  // contractuelle importée sert de repli.
  const repEnVigueur = ligne?.mois[Math.max(0, Math.min(11, moisInitial - 1))]?.repartition ?? null;
  const fonctionDerivee = deriverFonction(repEnVigueur, categories);

  useEffect(() => {
    if (detail) {
      setInfoNom(detail.employe.nom);
      setInfoDebut(detail.employe.debut_activite ?? '');
      setInfoFin(detail.employe.fin_activite ?? '');
      setInfoNote(detail.employe.note ?? '');
    }
  }, [detail]);

  async function enregistrerInfos() {
    if (!infoCommentaire.trim() || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.put(`/salaires/employes/${employeId}`, {
        nom: infoNom,
        debutActivite: infoDebut || null,
        finActivite: infoFin || null,
        note: infoNote || null,
        commentaire: infoCommentaire.trim(),
      });
      setInfoCommentaire('');
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  async function supprimerEmploye() {
    if (!window.confirm(`Supprimer ${detail?.employe.nom} et tout son historique ? Cette action est irréversible.`)) return;
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    try {
      await api.delete(`/salaires/employes/${employeId}`, { commentaire: c.trim() });
      onModifie();
      onFermer();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  const apercu = nominalNum * tauxNum;

  return (
    <div className="modal-overlay" onClick={onFermer}>
      {/* Hauteur fixée sur le modal (le corps est en flex:1) : la taille ne
          change pas d'un onglet à l'autre */}
      <div className="modal modal-wide" style={{ maxWidth: 760, height: 'min(85vh, 820px)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{detail?.employe.nom ?? '…'}</div>
            <div className="text-xs text-ink-3">
              {fonctionDerivee ?? detail?.employe.categorie} · activité {fmtDateFr(detail?.employe.debut_activite ?? null)} – {fmtDateFr(detail?.employe.fin_activite ?? null)}
              {detail?.employe.note ? ` · ${detail.employe.note}` : ''}
            </div>
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>

        <div className="modal-body" style={{ overflowY: 'auto' }}>
          <div className="tabs mb-16">
            <button className={`tab${onglet === 'salaire' ? ' active' : ''}`} onClick={() => setOnglet('salaire')}>Salaire & taux</button>
            <button className={`tab${onglet === 'repartition' ? ' active' : ''}`} onClick={() => setOnglet('repartition')}>Répartition du temps</button>
            <button className={`tab${onglet === 'infos' ? ' active' : ''}`} onClick={() => setOnglet('infos')}>Infos</button>
          </div>

          {erreur && <div className="text-err mb-16">{erreur}</div>}

          {onglet === 'salaire' && (
            <>
              <div className="formulaire-changement mb-16">
                <div className="formulaire-changement__titre">Modifier à partir d'un mois donné</div>
                <div className="flex gap-12 flex-wrap items-end">
                  <div className="form-group">
                    <label className="form-label">À partir de</label>
                    <input type="month" className="form-input" value={moisEffet} onChange={(e) => setMoisEffet(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Salaire nominal mensuel (100 %)</label>
                    <input type="number" className="form-input" style={{ width: 140 }} min={0} step={0.05}
                      value={nominal} onChange={(e) => setNominal(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Taux d'occupation (%)</label>
                    <input type="number" className="form-input" style={{ width: 100 }} min={0} max={300} step={0.5}
                      value={tauxPct} onChange={(e) => setTauxPct(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Statut</label>
                    <ChoixTagScenario
                      tag={tag}
                      scenarioId={scenarioCible}
                      scenarios={scenarios}
                      onChange={(t, sc) => { setTag(t); setScenarioCible(sc); }}
                    />
                  </div>
                </div>
                <div className="form-group mt-8">
                  <label className="form-label">Commentaire (obligatoire)</label>
                  <input className="form-input w-full" placeholder="Motif de la décision ou du scénario…"
                    value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
                </div>
                <div className="flex items-center justify-between mt-8">
                  <span className="text-sm text-ink-2">
                    Dès {fmtMoisFr(`${moisEffet}-01`)} : <strong>CHF {fmtCHF2(apercu)}</strong> / mois
                    {celluleRef && (nominalChange || tauxChange) && (
                      <span className="text-ink-3"> (actuellement CHF {fmtCHF2(celluleRef.montant - celluleRef.treizieme)})</span>
                    )}
                  </span>
                  <button className="btn btn-accent" disabled={!formulaireValide || enCours} onClick={enregistrerChangement}>
                    {enCours ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
                {!nominalChange && !tauxChange && (
                  <div className="text-xs text-ink-3 mt-8">Modifier le nominal ou le taux pour créer un changement.</div>
                )}
              </div>

              <div className="flex gap-16" style={{ alignItems: 'flex-start' }}>
                <HistoriqueEvenements
                  titre="Salaire nominal (100 %)"
                  lignes={(detail?.salaireEvenements ?? []).map((e) => ({
                    id: e.id, mois: e.mois_effet, valeur: `CHF ${fmtCHF2(Number(e.montant))}`,
                    tag: e.tag, commentaire: e.commentaire,
                  }))}
                  onSupprimer={(id) => supprimerEvenement('salaire', id)}
                />
                <HistoriqueEvenements
                  titre="Taux d'occupation"
                  lignes={(detail?.tauxEvenements ?? []).map((e) => ({
                    id: e.id, mois: e.mois_effet, valeur: fmtPct(Number(e.taux)),
                    tag: e.tag, commentaire: e.commentaire,
                  }))}
                  onSupprimer={(id) => supprimerEvenement('taux', id)}
                />
              </div>
            </>
          )}

          {onglet === 'repartition' && (
            <div className="card p-16">
              <p className="text-sm text-ink-2">
                La répartition du temps se gère désormais dans le module <strong>RH</strong> : c'est là que le
                taux d'occupation se ventile entre les fonctions, et les fonctions de terrain entre les
                prestations. Salaires répond à « combien coûte cette personne », RH à « à quoi va son temps ».
              </p>
              <div className="flex justify-end mt-8">
                <button className="btn btn-accent btn-sm" onClick={() => { window.location.hash = 'rh'; }}>
                  Ouvrir RH
                </button>
              </div>
            </div>
          )}


          {onglet === 'infos' && detail && (
            <div className="flex flex-col gap-12">
              <div className="flex gap-12 flex-wrap">
                <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
                  <label className="form-label">Nom & prénom</label>
                  <input className="form-input w-full" value={infoNom} onChange={(e) => setInfoNom(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Fonction (dérivée de la répartition)</label>
                  {fonctionDerivee ? (
                    <div className="flex gap-4 items-center flex-wrap" style={{ paddingTop: 4 }}>
                      <span className="badge badge-accent">{fonctionDerivee}</span>
                      {repEnVigueur && Object.entries(repEnVigueur.lignes).map(([catId, t]) => (
                        <span key={catId} className="badge badge-sm" title={nomCategorie(Number(catId))}>
                          {nomCategorie(Number(catId))} {fmtPct(Number(t))}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="flex gap-8 items-center" style={{ paddingTop: 4 }}>
                      <span className="badge">{detail.employe.categorie}</span>
                      <span className="text-xs text-ink-3">catégorie importée — définir une répartition par fonction pour la remplacer</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-12 flex-wrap">
                <div className="form-group">
                  <label className="form-label">Début d'activité</label>
                  <input type="date" className="form-input" value={infoDebut} onChange={(e) => setInfoDebut(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Fin d'activité <span className="text-ink-3">(borne le prorata du 13ᵉ)</span></label>
                  <input type="date" className="form-input" value={infoFin} onChange={(e) => setInfoFin(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Note</label>
                <input className="form-input w-full" value={infoNote} onChange={(e) => setInfoNote(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Commentaire de modification (obligatoire)</label>
                <input className="form-input w-full" value={infoCommentaire} onChange={(e) => setInfoCommentaire(e.target.value)} />
              </div>
              <div className="flex items-center justify-between">
                <button className="btn btn-danger" onClick={supprimerEmploye}>Supprimer l'employé·e</button>
                <button className="btn btn-accent" disabled={!infoCommentaire.trim() || enCours} onClick={enregistrerInfos}>
                  {enCours ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HistoriqueEvenements({ titre, lignes, onSupprimer }: {
  titre: string;
  lignes: Array<{ id: number; mois: string; valeur: string; tag: Tag; commentaire: string }>;
  onSupprimer: (id: number) => void;
}) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <h3 className="text-base font-semi mb-8">{titre}</h3>
      <table className="table table-compact">
        <thead>
          <tr><th>Dès</th><th>Valeur</th><th>Statut</th><th style={{ width: 32 }} /></tr>
        </thead>
        <tbody>
          {lignes.slice().reverse().map((l) => (
            <tr key={l.id} title={l.commentaire}>
              <td className="tabular-nums">{fmtMoisFr(l.mois)}</td>
              <td className="tabular-nums">{l.valeur}</td>
              <td><BadgeTag tag={l.tag} /></td>
              <td>
                <button className="btn btn-icon" title="Supprimer l'événement" onClick={() => onSupprimer(l.id)}>
                  <Trash2 className="icon-sm" />
                </button>
              </td>
            </tr>
          ))}
          {lignes.length === 0 && <tr><td colSpan={4} className="text-ink-3">Aucun événement.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
